// react
// dependencies
import { Map } from "immutable";
import PropTypes from "prop-types";
import React from "react";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import EmployeePicker from "../../../../../components/EmployeePicker/compatibility";
// components
import Icon from "../../../../../components/Icon";
import TextInput from "../../../../../components/Inputs/TextInput";
import SignatureSteps from "./SignatureSteps";

import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

// utils
import ForceInt from "../../../../../utils/ForceInt";

// contexts
import { CompanyFunctionsContext } from "../../../../../contexts";
// config
import Config from "../../Config";

// style

/**
 * React component to wrap the document signing button so we can make use of hooks.
 */
const CompanyFunctionsContextConsumer = (props) => {
	const { todoDocument } = props;

	const { value: docSigningFeatureVisible } = useFeatureFlagQuery("ff-is-document-signing-enabled", true);

	return (
		<CompanyFunctionsContext.Consumer>
			{(companyFunctions) => {
				if (companyFunctions.getIn(["documentSigning", "state"]) === "enabled" && docSigningFeatureVisible) {
					return todoDocument;
				}

				return null;
			}}
		</CompanyFunctionsContext.Consumer>
	);
};

CompanyFunctionsContextConsumer.propTypes = {
	todoDocument: PropTypes.node.isRequired,
};

export default class Todo extends React.Component {
	static propTypes = {
		documentSigning: PropTypes.instanceOf(Map),
		onRemove: PropTypes.func.isRequired,
		onChange: PropTypes.func.isRequired,
		onOpenDocumentPicker: PropTypes.func.isRequired,
		onOpenViewDocument: PropTypes.func.isRequired,
		onSignatureStepChange: PropTypes.func.isRequired,
		id: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		actor: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Map)]).isRequired,
		daysToComplete: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.oneOf([""])]).isRequired,
		actorName: PropTypes.string.isRequired,
		actorActiveStatus: PropTypes.bool.isRequired,
	};

	static defaultProps = {
		documentSigning: null,
	};

	constructor(props) {
		super(props);
		this.state = {
			actorActiveStatus: props.actorActiveStatus,
		};
	}

	componentDidUpdate(prevProps) {
		if (this.props.documentSigning && !prevProps.documentSigning) {
			this.setState({ actorActiveStatus: true });
		}
		if (this.props.actorActiveStatus !== prevProps.actorActiveStatus) {
			this.setState({ actorActiveStatus: this.props.actorActiveStatus });
		}
	}

	handleRemove = () => {
		//
		this.props.onRemove(this.props.id);
	};

	handleOpenDocumentPicker = () => {
		const { id, documentSigning } = this.props;

		if (documentSigning) {
			this.props.onOpenViewDocument(id);
		} else {
			this.props.onOpenDocumentPicker(id);
		}
	};

	handleChange = (val, label) => {
		let value = val;

		if (label === "daysToComplete" && value.length > 0) {
			value = ForceInt(value);
		}

		this.props.onChange(value, label);
	};

	handleDaysBlur = () => {
		const { daysToComplete } = this.props;

		const newValue = daysToComplete.toString().length > 0 ? ForceInt(daysToComplete) : "";
		this.props.onChange(newValue, "daysToComplete");
	};

	handleSignatureStepChange = (stepIndex, value) => {
		this.props.onSignatureStepChange(stepIndex, value);
	};

	render() {
		const { label, actor, daysToComplete, documentSigning, actorName, actorActiveStatus } = this.props;
		const hasDocumentAttached = documentSigning !== null;

		const todoDocument = hasDocumentAttached ? (
			<div
				onClick={this.handleOpenDocumentPicker}
				className={`absolute left-[30px] top-0 flex h-8 w-[30px] cursor-pointer items-center justify-center text-center text-primary-5 ${documentSigning ? "text-primary-5" : "text-neutral-4 hover:text-primary-5"}`}
			>
				<Icon type="fa5" name={["fas", "file-pdf"]} size="1x" />
			</div>
		) : (
			<div
				title="Attach Document"
				onClick={this.handleOpenDocumentPicker}
				className={`absolute left-[30px] top-0 flex h-8 w-[30px] cursor-pointer items-center justify-center text-center text-neutral-4 hover:text-primary-5`}
			>
				<Icon type="fa5" name={["fal", "file-pdf"]} size="1x" />
			</div>
		);

		const showSignatureSteps = true;

		return (
			<div className={`min-h-8 bg-white px-1.5 py-1.5 ${hasDocumentAttached ? "h-auto" : ""}`}>
				<div>
					<div className="relative">
						<div
							onClick={this.handleRemove}
							className="absolute left-0 top-0 flex h-8 w-[30px] cursor-pointer items-center justify-center text-center text-neutral-4 hover:text-red-5"
						>
							<Icon type="fa5" name={["far", "times-circle"]} size="1x" />
						</div>

						<CompanyFunctionsContextConsumer todoDocument={todoDocument} />

						<div className="ml-[60px] mr-[350px]">
							<div className="mx-1.5">
								<TextInput
									name="todo-label"
									value={label}
									onChange={(value) => this.handleChange(value, "label")}
									className={"inputContainer"}
									showWarning={label === "" || this.state.actorActiveStatus === false}
								/>
								{this.state.actorActiveStatus === false && (
									<div className="mb-2 mt-2 h-4 text-sm font-semibold leading-4 text-red-6">
										<FontAwesomeIcon icon={faExclamationCircle} color={"var(--py-red-6)"} /> Actor:{" "}
										{actorName} has been deactivated. Please assign another actor to continue.
									</div>
								)}
							</div>
						</div>

						<div className="absolute right-[120px] top-0 h-8 w-[220px] text-center">
							{hasDocumentAttached ? (
								<div className="text-[13px] italic leading-8 text-neutral-6">Signature Template</div>
							) : (
								<div className="mx-1.5">
									<EmployeePicker
										prependOptions={Config.todoPrependOptions}
										value={actor}
										onChange={(value) => this.handleChange(value, "actor")}
										showWarning={actor === "" || this.state.actorActiveStatus === false}
									/>
								</div>
							)}
						</div>

						<div className="absolute right-0 top-0 h-8 w-[120px] text-center">
							<div className="mx-1.5">
								<TextInput
									name="todo-days"
									onBlur={this.handleDaysBlur}
									value={daysToComplete}
									onChange={(value) => this.handleChange(value, "daysToComplete")}
									showWarning={daysToComplete === "" || this.state.actorActiveStatus === false}
								/>
							</div>
						</div>
					</div>

					{showSignatureSteps && hasDocumentAttached ? (
						<SignatureSteps
							name={documentSigning.get("name")}
							onChange={this.handleSignatureStepChange}
							steps={documentSigning.get("steps")}
						/>
					) : (
						""
					)}
				</div>
			</div>
		);
	}
}
